import React, { useState, useEffect } from "react";
import classes from './PaymentVoucher.module.css';
import HeaderNav from '../../../Components/Dahboard Header/HeaderNav/HeaderNav';
import Footer from "../../../Components/Footer/Footer";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../../Components/API/Api";
import { useUser } from '../../../UserContext';
import {
    BarChart2,
    Filter,
    CalendarClock,
    Mail,
    ArrowLeft,
    Printer,
    Download,
    Settings
} from 'lucide-react';

const ComputationPayslipReciept = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const token = localStorage.getItem("authToken");
    const [ledgerData, setLedgerData] = useState([]);
    const [loading, setLoading] = useState(true);
    const { user, isAuthenticated, logout } = useUser();

    // Debug: Log user data to see what's available
    console.log('DashboardHeader - User data:', user);
    console.log('DashboardHeader - Is authenticated:', isAuthenticated);

    // Get user's display name - fallback to 'User' if no name is available
    const displayName = user?.name || user?.firstName || user?.email?.split('@')[0] || 'User';

    console.log('DashboardHeader - Display name:', displayName);
    // new: store logged-in company info
    const [companyInfo, setCompanyInfo] = useState({
        name: "",
        address: "",
        email: "",
        phone: "",
        logo: ""
    });

    // NEW: base currency code & symbol (set from company settings)
    const [baseCurrencyCode, setBaseCurrencyCode] = useState(null);
    const [baseCurrencySymbol, setBaseCurrencySymbol] = useState(null);

    // NEW: read payment passed via navigate state — declare before any useEffect that references it
    const payment = location.state?.payment || null;

    // NEW: salary computation data
    const [salaryComputationData, setSalaryComputationData] = useState(null);
    const [salaryComputationLoading, setSalaryComputationLoading] = useState(false);
    const [selectedEmployeeIndex, setSelectedEmployeeIndex] = useState(0);

    const typeMap = {
        "1": "Assets",
        "2": "Liabilities",
        "3": "Equity",
        "4": "Revenue",
        "5": "Expenses"
    };

    const categoryMap = {
        "1": "Current Assets",
        "2": "Non-current Assets",
        "3": "Current Liabilities",
        "4": "Non-current Liabilities",
        "5": "Equity",
        "6": "Operating Revenue",
        "7": "Cost of Sales",
        "8": "Operating Expenses"
    };


    const formatMoney = (value) => {
        const num = Number(value || 0);
        return num.toLocaleString("en-NG", { minimumFractionDigits: 2 });
    };

    useEffect(() => {
        const fetchAccounts = async () => {
            try {
                const response = await api.get('/v1/chart_of_account');

                const accounts = Array.isArray(response.data.data?.data) ? response.data.data.data : [];
                const mappedData = accounts.map((account, index) => ({
                    id: account.id || `account-${index}`,
                    ref: account.gl_code || `REF-${index}`,
                    description: account.gl_name,
                    type: typeMap[account.class_id] || "Unknown",
                    category: categoryMap[account.category_id] || "Unknown",
                    balance: formatMoney(account.balance || 0),
                    status: account.status === "1" ? "Active" : "Inactive",
                }));


                setLedgerData(mappedData);
            } catch (error) {
                console.error("Error fetching chart of accounts:", error);
            } finally {
                setLoading(false);
            }
        };
        if (token) fetchAccounts();
        else setLoading(false);
    }, [token]);

    // new: fetch company info for header display
    useEffect(() => {
        const fetchCompanyInfo = async () => {
            if (!token) return;
            try {
                const res = await api.get('/v1/companies/get-info');
                const item = Array.isArray(res?.data?.data) ? res.data.data[0] : (res?.data?.data || null);
                if (item) {
                    setCompanyInfo({
                        name: item.name || "",
                        address: item.address || "",
                        email: item.email || "",
                        phone: item.phone_number || "",
                        logo: item.logo || ""
                    });

                    // NEW: resolve base currency from company info if present
                    const currencyId = item?.currency_base;
                    if (currencyId) {
                        try {
                            const currenciesRes = await api.get('/v1/fetch-all-currencies');
                            const currencies = Array.isArray(currenciesRes?.data?.data) ? currenciesRes.data.data : (currenciesRes?.data?.data || []);
                            const baseCurrency = currencies.find(c => String(c.id) === String(currencyId));
                            if (baseCurrency) {
                                setBaseCurrencyCode(baseCurrency.code || null);
                                setBaseCurrencySymbol(baseCurrency.symbol || null);
                            }
                        } catch (curErr) {
                            console.error("Failed to fetch currencies for base currency", curErr);
                        }
                    }
                }
            } catch (err) {
                console.error("Failed to fetch company info", err);
            }
        };
        fetchCompanyInfo();
    }, [token]);

    // NEW: fetch salary computation data
    useEffect(() => {
        const fetchSalaryComputation = async () => {
            if (!token) return;
            try {
                setSalaryComputationLoading(true);
                const res = await api.get('/v1/salary-computation');
                
                if (res?.data) {
                    setSalaryComputationData(res.data);
                    console.log('Salary computation data:', res.data);
                } else {
                    setSalaryComputationData(null);
                }
            } catch (err) {
                console.error("Error fetching salary computation data:", err);
                setSalaryComputationData(null);
            } finally {
                setSalaryComputationLoading(false);
            }
        };
        
        fetchSalaryComputation();
    }, [token]);

    // NEW: helper to title-case a name
    const titleCase = (s) => {
        if (!s || typeof s !== "string") return s;
        return s
            .split(" ")
            .map(p => p.trim())
            .filter(Boolean)
            .map(word => word[0]?.toUpperCase() + word.slice(1).toLowerCase())
            .join(" ");
    };

    // NEW: helper to synchronously get stored user display name (localStorage / sessionStorage)
    const getStoredUserName = () => {
        // First try the exact key HeaderNav appears to use
        try {
            const rawUser = localStorage.getItem("user");
            if (rawUser) {
                try {
                    const parsedUser = JSON.parse(rawUser);
                    const candidate =
                        parsedUser?.name ||
                        parsedUser?.data?.name ||
                        parsedUser?.user?.name ||
                        parsedUser?.full_name ||
                        parsedUser?.username;
                    if (candidate) return titleCase(String(candidate));
                    // raw string fallback
                } catch {
                    if (rawUser.trim()) return titleCase(rawUser.trim());
                }
            }
        } catch {
            // ignore and continue to other keys
        }

        const checkStorage = (storage) => {
            const possibleKeys = ["userContext", "userInfo", "authUser", "authUserInfo", "currentUser", "profile", "userProfile"];
            for (const key of possibleKeys) {
                const raw = storage.getItem(key);
                if (!raw) continue;
                try {
                    const parsed = JSON.parse(raw);
                    const candidate =
                        parsed?.name ||
                        parsed?.full_name ||
                        parsed?.username ||
                        (parsed?.data && (parsed.data.name || parsed.data.full_name)) ||
                        (parsed?.user && (parsed.user.name || parsed.user.full_name)) ||
                        (parsed?.first_name && parsed?.last_name && `${parsed.first_name} ${parsed.last_name}`);
                    if (candidate) return titleCase(String(candidate));
                } catch {
                    if (raw && raw.trim()) return titleCase(raw.trim());
                }
            }
            return null;
        };

        return checkStorage(localStorage) || checkStorage(sessionStorage) || null;
    };

    // NEW: prefer logged-in user's display name for "Prepared By" (initialize from storage)
    const [preparedByName, setPreparedByName] = useState(() => getStoredUserName() || "");

    // Keep preparedByName in sync when other code updates localStorage (e.g., HeaderNav)
    useEffect(() => {
        const handleStorage = (event) => {
            if (!event) return;
            const watchedKeys = ["user", "userContext", "userInfo", "authUser", "authUserInfo", "currentUser", "profile", "userProfile"];
            if (watchedKeys.includes(event.key)) {
                setPreparedByName(getStoredUserName() || "");
            }
        };
        window.addEventListener("storage", handleStorage);
        // also refresh on mount in case HeaderNav updated after this component mounted
        setPreparedByName(getStoredUserName() || preparedByName || "");
        return () => window.removeEventListener("storage", handleStorage);
    }, []);

    // POLL: same-tab updates to localStorage (HeaderNav may set user after this component mounted)
    useEffect(() => {
        if (preparedByName) return;
        let attempts = 0;
        const maxAttempts = 10; // 10 * 500ms = 5s max
        const intervalId = setInterval(() => {
            const name = getStoredUserName();
            if (name) {
                setPreparedByName(name);
                clearInterval(intervalId);
                return;
            }
            attempts += 1;
            if (attempts >= maxAttempts) {
                clearInterval(intervalId);
            }
        }, 500);

        return () => clearInterval(intervalId);
    }, [preparedByName]);

    const pageMap = {
        '/dashboard': 'Dashboard',
        '/transactions': 'Transactions',
        '/accounts': 'Accounting',
        '/reports': 'Reports',
        '/banking': 'Banking',
        '/settings': 'Settings',
    };

    const handleMail = () => alert("Mail clicked!");
    const handlePrinter = () => window.print();
    const handleDownload = () => {
        const csvContent = [
            ["Ref", "Description", "Type", "Category", "Balance", "Status"],
            ...ledgerData.map(d => [d.ref, d.description, d.type, d.category, d.balance, d.status])
        ].map(e => e.join(",")).join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", "chart_of_accounts.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };
    const handleSettings = () => navigate("/settings/chart-ledger");

    // helper to format date safely
    const formatDate = (dateStr) => {
        if (!dateStr) return "N/A";
        const d = new Date(dateStr);
        if (isNaN(d)) return dateStr;
        return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    };

    // helper to format money using existing formatMoney
    const formatCurrency = (value) => {
        return formatMoney(value);
    };

    // derive receipt-related values from payment with fallbacks
    const receiptNo = payment?.receipt_number || payment?.invoice_number || payment?.uuid || "N/A";
    const receiptDate = payment?.transaction_date || payment?.date_of_invoice || payment?.created_at || null;
    const receivedFrom = payment?.particular || payment?.supplier?.name || payment?.supplier || "N/A";
    const paymentMethod = payment?.mode?.name || (payment?.payment_mode
        ? (String(payment.payment_mode) === "1" ? "Bank Transfer" : String(payment.payment_mode) === "2" ? "Cheque" : `Method ${payment.payment_mode}`)
        : "N/A");
    const referenceNo = payment?.invoice_number || payment?.uuid || "N/A";

    // NEW: compute payslip month/year if available (handles "YYYY-MM" and other formats)
    const formatYYYYMM = (s) => {
        if (!s) return "";
        // 1) "YYYY-MM" case
        if (/^\d{4}-\d{2}$/.test(s)) {
            const [y, m] = s.split('-');
            const d = new Date(Number(y), Number(m) - 1, 1);
            return d.toLocaleString('en-GB', { month: 'long', year: 'numeric' });
        }
        // 2) ISO date or other parseable date
        const dt = new Date(s);
        if (!isNaN(dt)) return dt.toLocaleString('en-GB', { month: 'long', year: 'numeric' });
        // otherwise return original
        return s;
    };
    const payslipMonthYear = formatYYYYMM(
        salaryComputationData?.date ||
        payment?.computation_date ||
        payment?.date ||
        payment?.period ||
        payment?.data?.date ||
        payment?.payslip_date
    );

    // Helper to clean strings (remove newlines, extra spaces)
    const cleanString = (s) => typeof s === "string" ? s.replace(/\s+/g, " ").replace(/\n/g, " ").trim() : "";

    // NEW: get selected employee from salary computation data or fallback to payment
    const selectedEmployee = salaryComputationData?.data && Array.isArray(salaryComputationData.data) && salaryComputationData.data[selectedEmployeeIndex]
        ? salaryComputationData.data[selectedEmployeeIndex]
        : null;

    // Fix: Use selected employee from computation data first, then payment, then fallback
    const employeeInfo = (() => {
        if (selectedEmployee) return selectedEmployee;
        if (payment?.employee_id && payment?.name && payment?.salary_structure) return payment;
        if (Array.isArray(payment?.data?.data) && payment.data.data.length > 0) return payment.data.data[0];
        if (Array.isArray(payment?.data) && payment.data.length > 0) return payment.data[0];
        if (payment?.employee) return payment.employee;
        if (payment?.supplier && typeof payment.supplier === 'object') return payment.supplier;
        return {};
    })();

    // Employee details extraction (never show "N/A")
    const employeeName = cleanString(employeeInfo?.name || employeeInfo?.full_name || employeeInfo?.employee_name || receiptNo);
    const employeeId = employeeInfo?.employee_id || employeeInfo?.id || "";
    const employeeDept = cleanString(employeeInfo?.department || employeeInfo?.dept || receivedFrom);
    const employeeAccount = employeeInfo?.account_number || employeeInfo?.bank_account || paymentMethod || "";
    const employeeGender = employeeInfo?.gender || "";
    const employeeGrade = employeeInfo?.grade || employeeInfo?.salary_structure?.grade || "";
    const employeeStep = employeeInfo?.step || "";
    const employeeDesignation = cleanString(employeeInfo?.designation || employeeInfo?.job_title || "");
    
    // NEW: bank details from salary computation
    const employeeBankName = employeeInfo?.bank_name || employeeInfo?.bank || "";
    const employeeBankCode = employeeInfo?.bank_code || "";
    const employeeBankAccount = employeeInfo?.account_number || employeeInfo?.bank_account || "";

    // NEW: prefer salary structure from employeeInfo if available, otherwise fallback to payment salary_structure
    const salaryStruct = employeeInfo?.salary_structure || payment?.salary_structure || {};

    const rawAllowances = Array.isArray(salaryStruct?.allowances) ? salaryStruct.allowances : [];
    const rawDeductions = Array.isArray(salaryStruct?.deductions) ? salaryStruct.deductions : [];

    const getRowAmount = (row) => Number(row?.amount ?? row?.value ?? 0) || 0;
    const isTaxRow = (row) => {
        const name = String(row?.type_name ?? row?.description ?? "").toLowerCase();
        return name.includes("tax");
    };

    // Some payloads put Tax under allowances; force it under deductions so it appears
    // in the right-hand "Deductions" columns of the slip.
    const taxAllowances = rawAllowances.filter(isTaxRow);
    const allowances = rawAllowances.filter((a) => !isTaxRow(a));

    const dedupeKey = (row) =>
        `${String(row?.type_name ?? row?.description ?? "").toLowerCase()}|${getRowAmount(row)}`;
    const existingDeductionKeys = new Set(rawDeductions.map(dedupeKey));
    const taxToAppend = taxAllowances.filter((a) => !existingDeductionKeys.has(dedupeKey(a)));
    let deductions = [...rawDeductions, ...taxToAppend];

    // Your API can also provide tax as `salary_structure.tax` while `deductions` is empty.
    // Ensure Tax always shows in the deductions columns.
    const taxAmount = salaryStruct?.tax;
    const alreadyHasTaxRow = deductions.some((row) =>
        String(row?.type_name ?? row?.description ?? "").toLowerCase().includes("tax")
    );
    if (taxAmount !== undefined && taxAmount !== null && !alreadyHasTaxRow) {
        deductions = [
            ...deductions,
            { type_name: "Tax", amount: taxAmount }
        ];
    }

    const firstDeduction = deductions.length > 0 ? deductions[0] : null;
    const remainingDeductions = deductions.length > 1 ? deductions.slice(1) : [];
    const remainingMapLength = Math.max(allowances.length, remainingDeductions.length);

    // keep old numeric fallbacks for single-line payments
    const lineAmountValue = parseFloat(payment?.total_currency_amount ?? payment?.currency_amount ?? payment?.amount ?? 0);
    const subtotalValue = lineAmountValue;
    const totalValue = parseFloat(payment?.total_converted_amount ?? payment?.total_currency_amount ?? subtotalValue);

    // Use base currency (from company) first, then payment currency, then defaults
    const displayCurrencyCode = baseCurrencyCode || payment?.currency?.code || "NGN";
    const displayCurrencySymbol = baseCurrencySymbol || payment?.currency?.symbol || "₦";

    // NEW: helper functions to get currency code/symbol (avoid linter no-undef issues in JSX)
    const getDisplayCurrencyCode = () => displayCurrencyCode;
    const getDisplayCurrencySymbol = () => displayCurrencySymbol;

    // NEW: convert numbers to words (handles integer + two-decimal fractional part)
    const integerToWords = (n) => {
        if (n === 0) return "zero";
        const units = ["", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine",
            "ten", "eleven", "twelve", "thirteen", "fourteen", "fifteen", "sixteen",
            "seventeen", "eighteen", "nineteen"];
        const tens = ["", "", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety"];
        const scales = [
            { value: 1e12, name: "trillion" },
            { value: 1e9, name: "billion" },
            { value: 1e6, name: "million" },
            { value: 1e3, name: "thousand" },
            { value: 1e2, name: "hundred" }
        ];

        const words = [];

        const helper = (num) => {
            let str = "";
            if (num < 20) return units[num];
            if (num < 100) {
                const t = Math.floor(num / 10);
                const u = num % 10;
                return tens[t] + (u ? "-" + units[u] : "");
            }
            for (let i = 0; i < scales.length; i++) {
                const { value, name } = scales[i];
                if (num >= value) {
                    const count = Math.floor(num / value);
                    const remainder = num % value;
                    if (value === 100) {
                        str += helper(count) + " " + name;
                    } else {
                        str += helper(count) + " " + name;
                    }
                    if (remainder) str += " " + helper(remainder);
                    return str;
                }
            }
            return str;
        };

        return helper(n).trim();
    };

    const convertAmountToWords = (amount, currencyCode) => {
        const absAmount = Math.abs(Number(amount) || 0);
        const integerPart = Math.floor(absAmount);
        const fractionPart = Math.round((absAmount - integerPart) * 100); // two decimals

        // currency names and fractional unit names map (add more as needed)
        const currencyNameMap = {
            NGN: { name: "Naira", fraction: "kobo" },
            USD: { name: "Dollar", fraction: "cents" },
            EUR: { name: "Euro", fraction: "cents" },
            GBP: { name: "Pound", fraction: "pence" }
        };
        const currencyInfo = currencyNameMap[currencyCode] || { name: currencyCode || "Units", fraction: "cents" };

        const intWords = integerToWords(integerPart);
        let words = "";

        if (integerPart === 0 && fractionPart === 0) {
            words = `Zero ${currencyInfo.name}`;
        } else if (fractionPart === 0) {
            words = `${intWords} ${currencyInfo.name}`;
        } else if (integerPart === 0) {
            const fracWords = integerToWords(fractionPart);
            words = `${fracWords} ${currencyInfo.fraction} ${currencyInfo.name.startsWith(currencyCode) ? "" : ""}`.trim();
            // prefer "X kobo" when no integer part
        } else {
            const fracWords = integerToWords(fractionPart);
            words = `${intWords} ${currencyInfo.name} and ${fracWords} ${currencyInfo.fraction}`;
        }

        // Capitalize first letter and append "Only"
        return words.replace(/^\w/, c => c.toUpperCase()) + " Only";
    };

    // Use helper instead of direct variable to avoid ESLint no-undef errors
    const amountInWords = convertAmountToWords(totalValue, getDisplayCurrencyCode());

    // Calculate totals from the rows we actually display (so "Tax under deductions"
    // stays consistent with the totals).
    const basicSalaryAmount = Number(salaryStruct.basic_salary ?? 0) || 0;
    const totalEarnings = basicSalaryAmount + allowances.reduce((sum, a) => sum + getRowAmount(a), 0);
    const totalDeductions = deductions.reduce((sum, d) => sum + getRowAmount(d), 0);
    // Gross Pay should remain the gross earnings figure; deductions only affect Net Salary.
    const grossPay = totalEarnings;

    return (
        <div>
            <div className={`${classes.noPrint}`}>
                <HeaderNav activePage={pageMap[location.pathname]} />
            </div>

            <div className={classes.Container}>
                {/* Action Buttons */}
                <div className={classes.actions}>
                    <button className={classes.btns} onClick={() => navigate("/transactions/receivables/sales")}>
                        New Sales Invoice
                    </button>
                    <button className={classes.btns} onClick={() => navigate("/inventory/purchaseOrder")}>
                        New Purchase Invoice
                    </button>
                    <button className={classes.btns} onClick={() => navigate("/customer-sales")}>
                        All Invoices
                    </button>
                    <button className={`${classes.btns} ${classes.greens}`} onClick={() => navigate("/settings/product-services")}>
                        Product & Services
                    </button>
                </div>
                <div className={classes.act}>
                    <form className={classes.form}>
                        <div className={classes.group}>
                            <label>Period:</label>
                            <div className={classes.dropdownWrapper}>
                                <select className={classes.statusDropdown}>
                                    <option>Current month</option>
                                    <option>Last month</option>
                                    <option>This year</option>
                                    <option>Custom range</option>
                                </select>
                                <span className={classes.arrowDown}>▼</span>
                            </div>
                        </div>
                        <div className={classes.group1}>
                            <label>Group by:</label>
                            <div className={classes.dropdownWrapper}>
                                <select className={classes.statusDropdown}>
                                    <option>None</option> <option>Date</option>
                                    <option>Transaction Type</option>
                                    <option>Amount</option>
                                </select>
                                <span className={classes.arrowDown}>▼</span>
                            </div> </div> <div className={classes.group1}>
                            <label>Start Date:</label>
                            <div className={classes.dropdownWrapper}>
                                <select className={classes.statusDropdown}>
                                    <option>01/01/25</option> <option>01/12/24</option>
                                    <option>01/11/24</option> <option>01/10/24</option>
                                </select>
                                <span className={classes.arrowDown}>▼</span>
                            </div>
                        </div>
                        <div className={classes.group1}>
                            <label>End Date:</label>
                            <div className={classes.dropdownWrapper}>
                                <select className={classes.statusDropdown}>
                                    <option>{new Date().toLocaleDateString('en-GB')}</option>
                                    <option>31/12/24</option> <option>30/11/24</option>
                                    <option>31/10/24</option> </select>
                                <span className={classes.arrowDown}>▼</span>
                            </div>
                        </div>
                        <div className={classes.group1}>
                            <button className={classes.greenBtn}
                                type="button"> <BarChart2 size={16} style={{ marginRight: "6px" }}
                                />
                                Run Report
                            </button>
                        </div>
                    </form>
                    <div className={classes.iconbarr}>
                        <div className={classes.iconbar}>
                            <Filter size={16} style={{ marginRight: "6px" }}
                            />
                            Filter
                        </div>
                        <div className={classes.iconbar}>
                            <CalendarClock size={16} style={{ marginRight: "6px" }} />
                            Schedule
                        </div>
                    </div>
                </div>
                <div className={classes.matron}> <div>
                    <button className={classes.backButton}
                        onClick={() => navigate(-1)}> <ArrowLeft size={16} style={{ marginRight: '6px' }}
                        />
                    </button>
                </div>
                    <div>
                        <p className={classes.toplea}>Payslip</p>
                        <p className={classes.topl}>Payment as of {new Date().toLocaleDateString()}</p>
                    </div>
                </div>

                <div className={classes.containere}>
                    <div className={classes.iconbars}>
                        <div className={classes.iconbarss}>
                            <div className={classes.iconbar} onClick={handleMail} style={{ cursor: "pointer" }}>
                                <Mail size={16} />
                            </div>
                            <div className={classes.iconbar} onClick={handlePrinter} style={{ cursor: "pointer" }}>
                                <Printer size={16} />
                            </div>
                            <div className={classes.iconbar} onClick={handleDownload} style={{ cursor: "pointer" }}>
                                <Download size={16} />
                            </div>
                            <div className={classes.iconbar} onClick={handleSettings} style={{ cursor: "pointer" }}>
                                <Settings size={16} />
                            </div>
                        </div>
                    </div>
                    {/* replaced static header with dynamic companyInfo */}
                    <header className={classes.pr_header}>
                        <div className={classes.pr_logo}>
                            {/* show company logo if available else fallback image path */}
                            <img
                                src={companyInfo.logo || "/company-logo.png"}
                                alt={`${companyInfo.name || "Company"} Logo`}
                                className={classes.pr_logoImg}
                            />
                        </div>
                        <div className={classes.pr_companyInfo}>
                            <h1 className={classes.pr_companyName}>{companyInfo.name || "Company Name"}</h1>
                            <p className={classes.pr_companyAddr}>{companyInfo.address || "Company address not provided"}</p>
                            <p className={classes.pr_companyContact}>
                                {companyInfo.email ? `Email: ${companyInfo.email}` : "Email: N/A"}
                                {companyInfo.phone ? ` | Tel: ${companyInfo.phone}` : ""}
                            </p>
                        </div>
                    </header>
                    { /* replaced content starts */}
                    {/* <div className={classes.pr_voucher}> */}
                    {/* Payslip title and month/year together */}
                    <div style={{ textAlign: "center", marginBottom: 16 }}>
                        <div className={classes.pr_title} style={{ position: "relative", paddingBottom: 0 }}>
                            MONTHLY PAYSLIP
                            <div
                                style={{
                
                                    color: "#222325ff",
                                    fontSize: "12px",
                                    // margin: "6px auto 0 auto",
                                    padding: "2px 12px",
                                    borderRadius: "0 0 8px 8px",
                                    // display: "inline-block",
                                    minWidth: 80,
                                }}
                            >
                                {payslipMonthYear}
                            </div>
                        </div>
                    </div>

                    <table className={classes.pr_table}>
                        <tbody>
                            <tr>
                                <th className={classes.pr_label}>Employee's Name :</th>
                                <td className={classes.pr_value}>{employeeName}</td>

                                <th className={classes.pr_label}>Date of payment:</th>
                                <td className={classes.pr_value}>{payslipMonthYear}</td>
                                {/* <td className={classes.pr_value}>{formatDate(receiptDate)}</td> */}
                            </tr>
                            <tr>
                                <th className={classes.pr_label}>Department:</th>
                                <td className={classes.pr_value}>{employeeDept}</td>

                                <th className={classes.pr_label}>Gender:</th>
                                <td className={classes.pr_value}>{employeeGender}</td>
                                {/* <th className={classes.pr_label}>Account No:</th>
                                <td className={classes.pr_value}>{employeeAccount}</td> */}
                            </tr>
                            <tr>
                                {/* <th className={classes.pr_label}>Gender:</th>
                                <td className={classes.pr_value}>{employeeGender}</td> */}

                                {/* <th className={classes.pr_label}>Grade:</th>
                                <td className={classes.pr_value}>{employeeGrade}</td> */}
                            </tr>
                            <tr>
                                <th className={classes.pr_label}>Designation:</th>
                                <td className={classes.pr_value}>{employeeDesignation}</td>

                                <th className={classes.pr_label}>Step:</th>
                                <td className={classes.pr_value}>{employeeStep}</td>
                            </tr>
                            <tr>
                                <th className={classes.pr_label}>Employee ID:</th>
                                <td className={classes.pr_value}>{employeeId}</td>
                                {/* <th className={classes.pr_label}>Receipt No:</th>
                                <td className={classes.pr_value}>{receiptNo}</td> */}
                                <th className={classes.pr_label}>Grade:</th>
                                <td className={classes.pr_value}>{employeeGrade}</td>
                            </tr>
                        </tbody>
                    </table>

                    {/* NEW: Employee selector if multiple employees from salary computation */}
                    {salaryComputationData?.data && Array.isArray(salaryComputationData.data) && salaryComputationData.data.length > 1 && (
                        <div style={{ marginBottom: 16, padding: "12px", backgroundColor: "#f5f5f5", borderRadius: "4px" }}>
                            <label style={{ marginRight: "12px", fontWeight: 600 }}>Select Employee:</label>
                            <select
                                value={selectedEmployeeIndex}
                                onChange={(e) => setSelectedEmployeeIndex(Number(e.target.value))}
                                style={{
                                    padding: "8px 12px",
                                    borderRadius: "4px",
                                    border: "1px solid #ddd",
                                    fontSize: "14px",
                                    cursor: "pointer"
                                }}
                            >
                                {salaryComputationData.data.map((emp, idx) => (
                                    <option key={idx} value={idx}>
                                        {emp.employee_number} - {emp.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div className={classes.pr_sectionTitle}>Account Details</div>

                    {/* NEW: Bank details table before salary structure */}
                    {(employeeBankName || employeeBankCode || employeeBankAccount) && (
                        <table className={classes.pr_table}>
                            <tbody>
                                {employeeBankName && (
                                    <tr>
                                        <th className={classes.pr_label}>Bank Name:</th>
                                        <td className={classes.pr_value}>{employeeBankName}</td>
                                    </tr>
                                )}
                                {employeeBankCode && (
                                    <tr>
                                        <th className={classes.pr_label}>Bank Code:</th>
                                        <td className={classes.pr_value}>{employeeBankCode}</td>
                                    </tr>
                                )}
                                {employeeBankAccount && (
                                    <tr>
                                        <th className={classes.pr_label}>Account Number:</th>
                                        <td className={classes.pr_value}>{employeeBankAccount}</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}

                    {salaryStruct && Object.keys(salaryStruct).length > 0 ? (
                        <table className={classes.pr_table}>
                            <thead>
                                <tr>
                                    <th
                                        className={classes.pr_th}
                                        colSpan={2}
                                        style={{ fontSize: "1.125rem", fontWeight: 700, textAlign: "center" }}
                                    >
                                        Earnings
                                    </th>
                                    <th
                                        className={`${classes.pr_th} ${classes.pr_thDeduction}`}
                                        colSpan={2}
                                        style={{ fontSize: "1.125rem", fontWeight: 700, textAlign: "center" }}
                                    >
                                        Deductions
                                    </th>
                                </tr>
                                <tr>
                                    <th className={classes.pr_th} style={{ fontSize: "0.875rem", fontWeight: 600 }}>Description</th>
                                    <th className={classes.pr_th} style={{ fontSize: "0.875rem", fontWeight: 600, textAlign: "right" }}>Amount ({getDisplayCurrencySymbol()})</th>
                                    <th className={`${classes.pr_th} ${classes.pr_thDeduction}`} style={{ fontSize: "0.875rem", fontWeight: 600 }}>Description</th>
                                    <th className={`${classes.pr_th} ${classes.pr_thDeduction}`} style={{ fontSize: "0.875rem", fontWeight: 600, textAlign: "right" }}>Amount ({getDisplayCurrencySymbol()})</th>
                                </tr>
                            </thead>
                            <tbody>
                                {/* Basic Salary row — optionally show first deduction in the same row */}
                                <tr>
                                    <td className={classes.pr_td}>Basic Salary</td>
                                    <td className={classes.pr_td} style={{ textAlign: "right" }}>
                                        {formatCurrency(salaryStruct.basic_salary)}
                                    </td>
                                    <td className={classes.pr_td}>
                                        {firstDeduction ? (firstDeduction.type_name || "") : ""}
                                    </td>
                                    <td className={classes.pr_td} style={{ textAlign: "right" }}>
                                        {firstDeduction ? formatCurrency(firstDeduction.amount) : ""}
                                    </td>
                                </tr>

                                {/* Map over allowances and remaining deductions so we don't duplicate the first deduction */}
                                {Array.from({ length: remainingMapLength }).map((_, idx) => {
                                    const allowance = allowances[idx];
                                    const deduction = remainingDeductions[idx];
                                    return (
                                        <tr key={idx}>
                                            <td className={classes.pr_td}>{allowance?.type_name || ""}</td>
                                            <td className={classes.pr_td} style={{ textAlign: "right" }}>{allowance ? formatCurrency(allowance.amount) : ""}</td>
                                            <td className={classes.pr_td}>{deduction?.type_name || ""}</td>
                                            <td className={classes.pr_td} style={{ textAlign: "right" }}>{deduction ? formatCurrency(deduction.amount) : ""}</td>
                                        </tr>
                                    );
                                })}

                                {/* totals and rest of rows using salaryStruct */}
                                <tr>
                                    <td className={classes.pr_td} style={{ fontWeight: 700 }}>Total Earnings</td>
                                    <td className={classes.pr_td} style={{ textAlign: "right", fontWeight: 700 }}>{formatCurrency(totalEarnings)}</td>
                                    <td className={classes.pr_td} style={{ fontWeight: 700 }}>Total Deductions</td>
                                    <td className={classes.pr_td} style={{ textAlign: "right", fontWeight: 700 }}>{formatCurrency(totalDeductions)}</td>
                                </tr>
                                <tr>
                                  <td className={classes.pr_td} colSpan={3} style={{ textAlign: "right", fontWeight: 800 }}></td>
                                    <td className={classes.pr_td} style={{ textAlign: "right", fontWeight: 800 }}>
                                        {/* {getDisplayCurrencySymbol()}{formatCurrency(grossPay)} */}
                                    </td>  
                                </tr>
                                <tr>
                                    <td className={classes.pr_td} colSpan={3} style={{ textAlign: "right", fontWeight: 800, fontSize: "1.125rem" }}>Gross Pay</td>
                                    <td className={classes.pr_td} style={{ textAlign: "right", fontWeight: 800, fontSize: "1.125rem" }}>
                                        {/* {getDisplayCurrencySymbol()} */}
                                        {formatCurrency(grossPay)}
                                    </td>
                                </tr>
                                <tr>
                                    <td className={classes.pr_td} colSpan={3} style={{ textAlign: "right", fontWeight: 800, fontSize: "1.125rem" }}>Net Salary</td>
                                    <td className={classes.pr_td} style={{ textAlign: "right", fontWeight: 800, fontSize: "1.125rem" }}>
                                        {/* {getDisplayCurrencySymbol()} */}
                                        {formatCurrency(salaryStruct.net_salary ?? totalValue)}</td>
                                </tr>
                            </tbody>
                        </table>
                    ) : (
                        // fallback to existing payment-only layout
                        <table className={classes.pr_table}>
                            <thead>
                                <tr>
                                    <th className={classes.pr_th}>#</th>
                                    <th className={classes.pr_th} style={{ fontSize: "1.125rem", fontWeight: 700 }}>Earnings</th>
                                    <th className={classes.pr_th} style={{ fontSize: "1.125rem", fontWeight: 700, textAlign: "left" }}>Deductions</th>
                                </tr>
                                <tr>
                                    <th className={classes.pr_th}></th>
                                    <th className={classes.pr_th}>Description</th>
                                    <th className={classes.pr_th} style={{ textAlign: "right" }}>Amount ({getDisplayCurrencySymbol()})</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className={classes.pr_td}>1</td>
                                    <td className={classes.pr_td}>{payment?.description || payment?.narration || "Payment"}</td>
                                    <td className={classes.pr_td} style={{ textAlign: "right" }}>{getDisplayCurrencySymbol()}{formatCurrency(lineAmountValue)}</td>
                                </tr>
                                <tr>
                                    <td className={classes.pr_td} colSpan={2} style={{ textAlign: "right", fontWeight: 700 }}>Total Allowance</td>
                                    <td className={classes.pr_td} style={{ textAlign: "right" }}>{getDisplayCurrencySymbol()}{formatCurrency(subtotalValue)}</td>
                                </tr>
                                <tr>
                                    <td className={classes.pr_td} colSpan={2} style={{ textAlign: "right", fontWeight: 700 }}>Total Amount</td>
                                    <td className={classes.pr_td} style={{ textAlign: "right", fontWeight: 800 }}>{getDisplayCurrencySymbol()}{formatCurrency(totalValue)}</td>
                                </tr>
                            </tbody>
                        </table>
                    )}

                    {/* <table className={classes.pr_summary}>
                        <tbody>
                            <tr>
                                <td className={`${classes.pr_summaryLabel}`}>Amount in Words:</td>
                                <td className={classes.pr_summaryValue}>{amountInWords || "N/A"}</td>
                            </tr>
                            <tr>
                                <td className={`${classes.pr_summaryLabel}`}>Prepared By:</td>
                                <td className={classes.pr_summaryValue}>{preparedByName || "N/A"}</td>
                            </tr>
                            <tr>
                                <td className={`${classes.pr_summaryLabel}`}>Checked By:</td>
                                <td className={classes.pr_summaryValue}>{payment?.checked_by || "N/A"}</td>
                            </tr>
                            <tr>
                                <td className={`${classes.pr_summaryLabel}`}>Approved By:</td>
                                <td className={classes.pr_summaryValue}>{payment?.approved_by || "N/A"}</td>
                            </tr>
                            <tr>
                                <td className={`${classes.pr_summaryLabel}`}>Narration:</td>
                                <td className={classes.pr_summaryValue}>{payment?.narration || payment?.description || "N/A"}</td>
                            </tr>
                        </tbody>
                    </table> */}

                    <div className={classes.pr_chequeSection}>
                        <h4 className={classes.pr_chequeTitle}>Cheque Details</h4>
                        <table className={classes.pr_table}>
                            <tbody>
                                <tr>
                                    <th className={classes.pr_label} style={{ textAlign: 'right' }}>Cheque No</th>
                                    <td className={classes.pr_value}>{payment?.teller_number || payment?.receipt_number || "N/A"}</td>
                                    <th className={classes.pr_label} style={{ textAlign: 'right' }}>Bank</th>
                                    <td className={classes.pr_value}>{payment?.supplier?.bank_name || payment?.payment_bank || "N/A"}</td>
                                </tr>
                                <tr>
                                    <th className={classes.pr_label} style={{ textAlign: 'right' }}>Payee</th>
                                    <td className={classes.pr_value}>{receivedFrom}</td>
                                    <th className={classes.pr_label} style={{ textAlign: 'right' }}>Amount ({getDisplayCurrencyCode()})</th>
                                    <td className={classes.pr_value}>{getDisplayCurrencySymbol()}{formatCurrency(totalValue)}</td>
                                </tr>
                                <tr>
                                    <th className={classes.pr_label} style={{ textAlign: 'right' }}>Date</th>
                                    <td className={classes.pr_value}>{formatDate(payment?.date_logded || payment?.cheque_date || payment?.transaction_date)}</td>
                                    <th className={classes.pr_label} style={{ textAlign: 'right' }}>Signature</th>
                                    <td className={classes.pr_value}>_________________________</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div className={classes.pr_signatures}>
                        <div className={classes.pr_signBoxprpby}>
                            <div className={classes.dtresp}>{displayName}</div>
                            <div className={classes.pr_signBox}>Prepared By</div>
                        </div>

                        <div className={classes.pr_signBox}>Checked By</div>
                        <div className={classes.pr_signBox}>Approved By</div>
                    </div>

                    <div className={classes.pr_footer}>
                        <p className={classes.pr_note}><strong>Note:</strong> Attach the original cheque to this voucher before submission to Accounts for processing.</p>
                        <p className={classes.pr_generated}>Generated by Chequestream Accounting System © {new Date().getFullYear()}</p>
                    </div>
                    {/* </div> */}
                    { /* replaced content ends */}
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default ComputationPayslipReciept;
